import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Search,
  X,
  Check,
  Loader2,
  Sparkles,
  TestTube,
  Plus,
  ShieldCheck,
  Trash2,
  Layers,
} from 'lucide-react';
import {
  medicalRecordsApi,
  memedApi,
  doctorsApi,
  examsHubApi,
  type ExamItem as ApiExamItem,
  type ExamPriority,
  type ExameCatalogItem,
} from '@/api';
import { useAuth } from '@/contexts/AuthContext';
import { useDoctorProfile } from '@/hooks/doctor/useDoctorProfile';
import { useToast } from '@/providers/ToastProvider';
import { useMemedPrescription, extractMemedPrescriptionId } from '@/hooks/useMemedPrescription';
import { useMemedConfig } from '@/hooks/useMemedConfig';
import { mapPatientToMemed } from '@/hooks/memed/memed-patient.mapper';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

interface SelectedPatient {
  id: string;
  name: string;
  cpf: string;
}

export interface ExamBasketItem {
  id: string;
  name: string;
  material?: string;
  instructions?: string;
  tussCode?: string;
  susCode?: string;
  memedId?: string;
  category?: string;
  justification?: string;
}

interface ExamRequestPayload {
  medicalRecordId?: string;
  patientId: string;
  doctorId: string;
  appointmentId?: string;
  exams: ApiExamItem[];
  priority: ExamPriority;
  instructions?: string;
}

const COMMON_EXAMS_CANONICAL: ExamBasketItem[] = [
  {
    id: 'tuss-40304361',
    name: 'Hemograma completo com contagem de plaquetas',
    tussCode: '40304361',
    material: 'Sangue total com EDTA',
    category: 'Hematologia',
    instructions: 'Jejum desejável de 4 horas.',
  },
  {
    id: 'tuss-40302040',
    name: 'Glicemia de jejum',
    tussCode: '40302040',
    material: 'Plasma fluoretado / Soro',
    category: 'Bioquímica',
    instructions: 'Jejum obrigatório de 8 a 12 horas.',
  },
  {
    id: 'tuss-40302075',
    name: 'Hemoglobina glicada (HbA1c)',
    tussCode: '40302075',
    material: 'Sangue total com EDTA',
    category: 'Bioquímica',
    instructions: 'Jejum não obrigatório (3 horas recomendado).',
  },
  {
    id: 'tuss-40302318',
    name: 'Perfil Lipídico (Colesterol total, HDL, LDL, Triglicérides)',
    tussCode: '40302318',
    material: 'Soro',
    category: 'Bioquímica',
    instructions: 'Jejum de 12 horas recomendado.',
  },
  {
    id: 'tuss-40301639',
    name: 'Creatinina sérica',
    tussCode: '40301639',
    material: 'Soro',
    category: 'Bioquímica',
    instructions: 'Jejum de 4 horas. Evitar exercícios intensos prévios.',
  },
  {
    id: 'tuss-40302580',
    name: 'Ureia sérica',
    tussCode: '40302580',
    material: 'Soro',
    category: 'Bioquímica',
    instructions: 'Jejum de 4 horas.',
  },
  {
    id: 'tuss-40316521',
    name: 'Hormônio Tireoestimulante (TSH) Ultrassensível',
    tussCode: '40316521',
    material: 'Soro',
    category: 'Hormônios',
    instructions: 'Jejum de 4 horas. Coletar antes da medicação tireoidiana.',
  },
  {
    id: 'tuss-40316530',
    name: 'Tiroxina Livre (T4 Livre)',
    tussCode: '40316530',
    material: 'Soro',
    category: 'Hormônios',
    instructions: 'Jejum de 4 horas.',
  },
  {
    id: 'tuss-40302512',
    name: 'Transaminase Glutâmico Oxalacética (TGO / AST)',
    tussCode: '40302512',
    material: 'Soro',
    category: 'Função Hepática',
    instructions: 'Jejum de 4 horas. Evitar bebidas alcoólicas nas 24h anteriores.',
  },
  {
    id: 'tuss-40302520',
    name: 'Transaminase Glutâmico Pirúvica (TGP / ALT)',
    tussCode: '40302520',
    material: 'Soro',
    category: 'Função Hepática',
    instructions: 'Jejum de 4 horas.',
  },
  {
    id: 'tuss-40307816',
    name: 'Vitamina D - 25 Hidroxivitamina D (25-OH-D)',
    tussCode: '40307816',
    material: 'Soro',
    category: 'Vitaminas e Minerais',
    instructions: 'Jejum de 4 horas.',
  },
  {
    id: 'tuss-40302636',
    name: 'Vitamina B12 (Cianocobalamina)',
    tussCode: '40302636',
    material: 'Soro protegido da luz',
    category: 'Vitaminas e Minerais',
    instructions: 'Jejum de 8 horas.',
  },
  {
    id: 'tuss-40301931',
    name: 'Ferritina sérica',
    tussCode: '40301931',
    material: 'Soro',
    category: 'Hematologia / Ferro',
    instructions: 'Jejum de 4 horas.',
  },
  {
    id: 'tuss-40302407',
    name: 'Proteína C Reativa (PCR) Ultrassensível',
    tussCode: '40302407',
    material: 'Soro',
    category: 'Marcadores Inflamatórios',
    instructions: 'Jejum de 4 horas.',
  },
  {
    id: 'tuss-40311210',
    name: 'Urina tipo 1 (EAS / Sumário de Urina)',
    tussCode: '40311210',
    material: 'Primeira urina da manhã - Jato médio',
    category: 'Uroanálise',
    instructions: 'Higiene genital rigorosa prévia. Desprezar primeiro jato.',
  },
  {
    id: 'tuss-40310248',
    name: 'Urocultura com Antibiograma',
    tussCode: '40310248',
    material: 'Urina colhida estéril',
    category: 'Microbiologia',
    instructions: 'Jato médio em frasco estéril. Informar antibióticos em uso.',
  },
  {
    id: 'tuss-40310183',
    name: 'Parasitológico de Fezes (EPF)',
    tussCode: '40310183',
    material: 'Fezes frescas',
    category: 'Parasitologia',
    instructions: 'Sem laxantes ou contraste prévio.',
  },
  {
    id: 'tuss-40302385',
    name: 'Potássio sérico',
    tussCode: '40302385',
    material: 'Soro',
    category: 'Eletrólitos',
    instructions: 'Jejum de 4 horas.',
  },
  {
    id: 'tuss-40302458',
    name: 'Sódio sérico',
    tussCode: '40302458',
    material: 'Soro',
    category: 'Eletrólitos',
    instructions: 'Jejum de 4 horas.',
  },
  {
    id: 'tuss-40316335',
    name: 'PSA Total (Antígeno Prostático Específico)',
    tussCode: '40316335',
    material: 'Soro',
    category: 'Marcadores Tumorais',
    instructions: 'Jejum de 4h. Abstenção sexual por 48h.',
  },
];

interface ExamPreset {
  name: string;
  badge: string;
  exams: ExamBasketItem[];
}

const EXAM_PRESET_PROFILES: ExamPreset[] = [
  {
    name: '🩸 Check-up Adulto Completo',
    badge: '6 exames',
    exams: [
      COMMON_EXAMS_CANONICAL[0]!,
      COMMON_EXAMS_CANONICAL[1]!,
      COMMON_EXAMS_CANONICAL[3]!,
      COMMON_EXAMS_CANONICAL[4]!,
      COMMON_EXAMS_CANONICAL[6]!,
      COMMON_EXAMS_CANONICAL[14]!,
    ],
  },
  {
    name: '❤️ Cardiovascular & Metabólico',
    badge: '5 exames',
    exams: [
      COMMON_EXAMS_CANONICAL[1]!,
      COMMON_EXAMS_CANONICAL[2]!,
      COMMON_EXAMS_CANONICAL[3]!,
      COMMON_EXAMS_CANONICAL[13]!,
      COMMON_EXAMS_CANONICAL[4]!,
    ],
  },
  {
    name: '🦋 Tireoide, Ferro e Vitaminas',
    badge: '5 exames',
    exams: [
      COMMON_EXAMS_CANONICAL[6]!,
      COMMON_EXAMS_CANONICAL[7]!,
      COMMON_EXAMS_CANONICAL[10]!,
      COMMON_EXAMS_CANONICAL[11]!,
      COMMON_EXAMS_CANONICAL[12]!,
    ],
  },
  {
    name: '🩺 Função Hepática & Renal',
    badge: '5 exames',
    exams: [
      COMMON_EXAMS_CANONICAL[8]!,
      COMMON_EXAMS_CANONICAL[9]!,
      COMMON_EXAMS_CANONICAL[4]!,
      COMMON_EXAMS_CANONICAL[5]!,
      COMMON_EXAMS_CANONICAL[14]!,
    ],
  },
];

export function DoctorNewExamPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();

  const patientIdFromUrl = searchParams.get('patientId');
  const appointmentIdFromUrl = searchParams.get('appointmentId');
  const { data: doctorProfile } = useDoctorProfile(user?.id || '');
  const doctorId = doctorProfile?.id || user?.id || '';

  const [selectedPatient, setSelectedPatient] = useState<SelectedPatient | null>(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [showPatientSearch, setShowPatientSearch] = useState(false);

  const [examSearch, setExamSearch] = useState('');
  const [selectedExams, setSelectedExams] = useState<ExamBasketItem[]>([]);
  const [showExamSearch, setShowExamSearch] = useState(false);
  const [observations, setObservations] = useState('');
  const [priority, setPriority] = useState<'ROUTINE' | 'URGENT' | 'EMERGENCY'>('ROUTINE');
  const [creating, setCreating] = useState(false);
  const [isOpeningMemed, setIsOpeningMemed] = useState(false);

  const { openPrescription, onMemedEvent, loadScript } = useMemedPrescription();
  const { data: memedConfig } = useMemedConfig();

  // Consulta dinâmica no Catálogo Mestre TUSS 22
  const { data: catalogResults } = useQuery<ExameCatalogItem[]>({
    queryKey: ['exams-catalog', examSearch],
    queryFn: () => examsHubApi.searchCatalog(examSearch, undefined, 25),
    enabled: examSearch.trim().length > 1,
  });

  // Listener para capturar a assinatura digital MeMed (ICP-Brasil) com interoperabilidade TUSS
  useEffect(() => {
    const unsubscribePrinted = onMemedEvent('prescricaoImpressa', async (data: any) => {
      const memedId = extractMemedPrescriptionId(data);
      if (memedId && selectedPatient) {
        try {
          // Extrair itens de exames nativos e customizados entregues pela Memed
          const examesFromMemed = (data?.prescrição?.medicamentos || []).filter(
            (item: any) => item.tipo === 'exame' || item.custom_categoria === 'exame',
          );

          const itemsToIngest =
            examesFromMemed.length > 0
              ? examesFromMemed.map((e: any) => ({
                  memedId: e.id ? String(e.id) : undefined,
                  nome: e.nome,
                  tussCodigo: e.exames_tuss_codigo || null,
                  susCodigo: e.exames_sus_codigo || null,
                  indicacaoClinica: e.sanitized_posology || e.posologia || observations || 'Avaliação clínica',
                  preparoInstrucoes: e.instrucoes || undefined,
                }))
              : selectedExams.map((e) => ({
                  memedId: e.memedId,
                  nome: e.name,
                  tussCodigo: e.tussCode,
                  susCodigo: e.susCode,
                  indicacaoClinica: e.justification || observations || 'Avaliação clínica',
                  preparoInstrucoes: e.instructions,
                }));

          // 1. Ingestão no Hub de Interoperabilidade Medikei (TUSS + LIS)
          await examsHubApi.ingestExams({
            pacienteId: selectedPatient.id,
            medicoId: doctorId,
            prescriptionUuid: memedId,
            pdfUrl: data?.pdfUrl || undefined,
            signed: true,
            appointmentId: appointmentIdFromUrl || undefined,
            observacoesGerais: observations || undefined,
            exames: itemsToIngest,
          });

          // 2. Sincronização legado prontuário
          const docToken = await memedApi.getToken().then((r) => r.token).catch(() => undefined);
          await memedApi.syncMemedExam({
            appointmentId: appointmentIdFromUrl || undefined,
            patientId: selectedPatient.id,
            doctorId,
            memedPrescriptionId: memedId,
            exams: itemsToIngest.map((e: any) => ({
              name: e.nome,
              examName: e.nome,
              justification: e.indicacaoClinica,
            })),
            type: itemsToIngest.map((e: any) => e.nome).join(', ') || 'Exame Complementar',
            justification: observations || 'Avaliação clínica diagnóstica',
            doctorToken: docToken,
            pdfUrl: data?.pdfUrl,
          });

          queryClient.invalidateQueries({ queryKey: ['doctor', 'exams'] });
          queryClient.invalidateQueries({ queryKey: ['patient', 'exams'] });
          queryClient.invalidateQueries({ queryKey: ['medical-records'] });

          toast({
            title: 'Pedido com múltiplos exames emitido com ICP-Brasil!',
            description: `Total de ${itemsToIngest.length} exame(s) registrado(s) com códigos ANS/TUSS.`,
            variant: 'success',
          });
          navigate('/medico/exames');
        } catch (err) {
          console.error('[DoctorNewExamPage] Erro ao sincronizar exames MeMed:', err);
        }
      }
    });

    return () => {
      unsubscribePrinted();
    };
  }, [
    onMemedEvent,
    selectedPatient,
    appointmentIdFromUrl,
    doctorId,
    selectedExams,
    observations,
    navigate,
    queryClient,
    toast,
  ]);

  // Injeção de todos os exames da cesta na Memed
  const handleEmitWithMemed = useCallback(async () => {
    if (!selectedPatient) {
      toast({ title: 'Selecione um paciente', variant: 'error' });
      return;
    }
    if (selectedExams.length === 0) {
      toast({ title: 'Adicione pelo menos um exame ao pedido', variant: 'error' });
      return;
    }

    setIsOpeningMemed(true);
    try {
      let token: string | undefined;
      try {
        const tokenRes = await memedApi.getToken();
        token = tokenRes?.token;
      } catch (err) {
        console.warn('Erro ao obter token MeMed:', err);
      }

      await loadScript(token);

      const memedPatient = mapPatientToMemed({
        id: selectedPatient.id,
        name: selectedPatient.name,
        cpf: selectedPatient.cpf,
      } as any);

      // Mapeamento de todos os exames da cesta com códigos TUSS e preparo estruturado
      const examItems = selectedExams.map((exam) => {
        const indicacao = exam.justification || observations || 'Avaliação diagnóstica de rotina';
        const prep = exam.instructions ? `<br/><em>Preparo: ${exam.instructions}</em>` : '';
        const tussInfo = exam.tussCode ? `<br/><strong>Código TUSS (ANS):</strong> ${exam.tussCode}` : '';

        return {
          id: exam.memedId,
          nome: exam.tussCode ? `${exam.name} (TUSS ${exam.tussCode})` : exam.name,
          posologia: `<p><strong>Indicação clínica:</strong> ${indicacao}${tussInfo}${prep}</p>`,
          descricao: exam.material || 'Exame Laboratorial / Imagem',
        };
      });

      const result = await openPrescription(
        memedPatient,
        memedConfig?.workplace,
        [],
        [],
        { appointmentId: appointmentIdFromUrl || undefined },
        { items: examItems, token },
      );

      if (!result.success) {
        toast({ title: result.error || 'Erro ao abrir emissor MeMed', variant: 'error' });
      }
    } catch {
      toast({ title: 'Não foi possível inicializar a MeMed', variant: 'error' });
    } finally {
      setIsOpeningMemed(false);
    }
  }, [
    selectedPatient,
    selectedExams,
    observations,
    loadScript,
    openPrescription,
    memedConfig,
    appointmentIdFromUrl,
    toast,
  ]);

  useEffect(() => {
    if (patientIdFromUrl) {
      doctorsApi.getMyPatients(1, 100).then((response) => {
        const patient = response.patients.find((p) => p.id === patientIdFromUrl);
        if (patient) {
          setSelectedPatient({
            id: patient.id,
            name: patient.name,
            cpf: patient.cpf,
          });
        }
      });
    }
  }, [patientIdFromUrl]);

  const { data: patientsData } = useQuery({
    queryKey: ['doctor', 'patients', patientSearch],
    queryFn: () => doctorsApi.getMyPatients(1, 50, patientSearch || undefined),
    enabled: showPatientSearch && patientSearch.length > 0,
  });

  // Lista mesclada: resultados da API TUSS ou lista canônica
  const filteredExams = useMemo(() => {
    if (catalogResults && catalogResults.length > 0) {
      return catalogResults.map((c) => ({
        id: c.id,
        name: c.nome,
        tussCode: c.codigoTuss,
        susCode: c.codigoSigtap,
        memedId: c.memedId,
        material: c.tipoAmostra,
        category: c.categoria,
        instructions: c.preparoGeral,
      }));
    }

    if (!examSearch.trim()) return COMMON_EXAMS_CANONICAL.slice(0, 10);
    const search = examSearch.toLowerCase();
    return COMMON_EXAMS_CANONICAL.filter(
      (exam) =>
        exam.name.toLowerCase().includes(search) ||
        exam.material?.toLowerCase().includes(search) ||
        (exam.tussCode && exam.tussCode.includes(search)),
    ).slice(0, 20);
  }, [catalogResults, examSearch]);

  const handleSelectPatient = (patient: SelectedPatient) => {
    setSelectedPatient(patient);
    setShowPatientSearch(false);
    setPatientSearch('');
  };

  const handleAddExam = (exam: ExamBasketItem) => {
    if (!selectedExams.find((e) => (e.tussCode && e.tussCode === exam.tussCode) || e.name === exam.name)) {
      setSelectedExams((prev) => [
        ...prev,
        {
          ...exam,
          justification: observations || 'Indicação clínica diagnóstica',
        },
      ]);
    }
    setExamSearch('');
    setShowExamSearch(false);
  };

  const handleApplyPresetProfile = (preset: (typeof EXAM_PRESET_PROFILES)[0]) => {
    const newItems = preset.exams.filter(
      (p) => !selectedExams.some((s) => s.tussCode === p.tussCode || s.name === p.name),
    );

    if (newItems.length === 0) {
      toast({ title: 'Todos os exames deste perfil já estão na cesta', variant: 'info' });
      return;
    }

    setSelectedExams((prev) => [
      ...prev,
      ...newItems.map((item) => ({
        ...item,
        justification: observations || 'Investigação clínica preventiva',
      })),
    ]);

    toast({
      title: `${newItems.length} exames adicionados do perfil ${preset.name}`,
      variant: 'success',
    });
  };

  const handleRemoveExam = (identifier: string) => {
    setSelectedExams((prev) => prev.filter((e) => e.id !== identifier && e.tussCode !== identifier));
  };

  const handleUpdateItemJustification = (id: string, text: string) => {
    setSelectedExams((prev) =>
      prev.map((e) => (e.id === id ? { ...e, justification: text } : e)),
    );
  };

  const createExamRequestMutation = useMutation({
    mutationFn: async (data: ExamRequestPayload) => {
      return medicalRecordsApi.createExamRequest(data);
    },
  });

  const handleSubmit = async () => {
    if (!selectedPatient) {
      toast({ title: 'Selecione um paciente', variant: 'error' });
      return;
    }
    if (selectedExams.length === 0) {
      toast({ title: 'Adicione pelo menos um exame ao pedido', variant: 'error' });
      return;
    }

    setCreating(true);
    try {
      // 1. Ingestão direta no Hub de Exames (com múltiplos itens e TUSS)
      await examsHubApi.ingestExams({
        pacienteId: selectedPatient.id,
        medicoId: doctorId,
        appointmentId: appointmentIdFromUrl || undefined,
        observacoesGerais: observations || undefined,
        signed: false,
        exames: selectedExams.map((e) => ({
          nome: e.name,
          tussCodigo: e.tussCode,
          susCodigo: e.susCode,
          indicacaoClinica: e.justification || observations || 'Avaliação clínica',
          preparoInstrucoes: e.instructions,
        })),
      });

      // 2. Registro no serviço de prontuário legado
      await createExamRequestMutation.mutateAsync({
        medicalRecordId: undefined,
        patientId: selectedPatient.id,
        doctorId,
        appointmentId: appointmentIdFromUrl || undefined,
        exams: selectedExams.map((exam) => ({
          examName: exam.name,
          justification: exam.justification || observations || 'Exame solicitado',
          observations: exam.instructions,
        })),
        priority,
        instructions: observations,
      });

      queryClient.invalidateQueries({ queryKey: ['doctor', 'exams'] });
      queryClient.invalidateQueries({ queryKey: ['patient', 'exams'] });
      toast({
        title: 'Pedido de exames criado com sucesso!',
        description: `${selectedExams.length} exame(s) incluído(s) no mesmo pedido.`,
        variant: 'success',
      });
      navigate('/medico/exames');
    } catch (error) {
      toast({
        title: 'Erro ao criar pedido de exames',
        description: error instanceof Error ? error.message : 'Tente novamente',
        variant: 'error',
      });
    } finally {
      setCreating(false);
    }
  };

  const formatCpf = (cpf: string) => {
    const cleaned = cpf.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6, 9)}-${cleaned.slice(9)}`;
    }
    return cpf;
  };

  if (creating) {
    return <LoadingOverlay message="Processando pedido de exames..." />;
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl mx-auto">
      {/* Botão Voltar */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/medico/exames')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors font-medium text-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Voltar para Pedidos de Exame</span>
        </button>

        <Badge variant="outline" className="text-xs font-semibold px-3 py-1 bg-primary/5 text-primary border-primary/20">
          Interoperabilidade TUSS 22 ANS & Memed
        </Badge>
      </div>

      <div className="bg-card text-card-foreground rounded-2xl shadow-sm border border-border overflow-hidden">
        {/* Header do Card */}
        <div className="px-6 py-5 border-b border-border bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-primary/10 rounded-xl text-primary">
              <TestTube className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold text-foreground tracking-tight">
                Nova Requisição de Exames
              </h1>
              <p className="text-sm text-muted-foreground">
                Componha a cesta com múltiplos exames diagnósticos, códigos oficiais ANS/TUSS e assinatura digital ICP-Brasil.
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Seleção do Paciente */}
          <div>
            <label className="block text-sm font-bold text-foreground mb-2">
              Paciente Solicitante <span className="text-destructive">*</span>
            </label>
            {selectedPatient ? (
              <div className="flex items-center justify-between p-4 bg-primary/10 border border-primary/20 rounded-xl">
                <div>
                  <p className="font-bold text-foreground">{selectedPatient.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">CPF: {formatCpf(selectedPatient.cpf)}</p>
                </div>
                <button
                  onClick={() => setSelectedPatient(null)}
                  className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                  aria-label="Remover paciente selecionado"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar paciente por nome ou CPF..."
                    value={patientSearch}
                    onChange={(e) => {
                      setPatientSearch(e.target.value);
                      setShowPatientSearch(true);
                    }}
                    onFocus={() => setShowPatientSearch(true)}
                    className="pl-10 h-11 rounded-xl"
                  />
                </div>

                {showPatientSearch && patientsData && (
                  <div className="bg-card border border-border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    {patientsData.patients.length === 0 ? (
                      <div className="p-4 text-center text-muted-foreground text-sm">
                        Nenhum paciente encontrado
                      </div>
                    ) : (
                      patientsData.patients.map((patient) => (
                        <button
                          key={patient.id}
                          onClick={() =>
                            handleSelectPatient({
                              id: patient.id,
                              name: patient.name,
                              cpf: patient.cpf || '',
                            })
                          }
                          className="w-full px-4 py-3 text-left hover:bg-muted border-b border-border last:border-b-0 transition-colors flex items-center justify-between"
                        >
                          <div>
                            <p className="font-semibold text-foreground text-sm">{patient.name}</p>
                            <p className="text-xs text-muted-foreground">CPF: {patient.cpf || 'Não informado'}</p>
                          </div>
                          <Check className="h-4 w-4 text-primary opacity-0 group-hover:opacity-100" />
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Perfis Pré-Definidos (Atalhos Clínicos) */}
          <div>
            <div className="flex items-center gap-2 mb-2.5">
              <Sparkles className="h-4 w-4 text-amber-500" />
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Perfis Clínicos Frequentes (Inclusão Rápida em Lote)
              </label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
              {EXAM_PRESET_PROFILES.map((preset, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleApplyPresetProfile(preset)}
                  className="p-3 bg-muted/40 hover:bg-primary/10 border border-border hover:border-primary/40 rounded-xl text-left transition-all flex flex-col justify-between group shadow-xs"
                >
                  <span className="font-bold text-xs text-foreground group-hover:text-primary transition-colors">
                    {preset.name}
                  </span>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[11px] text-muted-foreground">{preset.badge}</span>
                    <span className="text-[11px] font-bold text-primary group-hover:underline flex items-center gap-0.5">
                      <Plus className="h-3 w-3" /> Adicionar
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Busca e Adição de Exames */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-bold text-foreground">
                Buscar e Adicionar Exames ao Pedido
              </label>
              <span className="text-xs text-muted-foreground">
                Catálogo TUSS 22 ANS + Laboratórios de Apoio
              </span>
            </div>

            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Digite o nome do exame ou código TUSS (ex: 40304361, Hemograma, Glicose, TSH)..."
                value={examSearch}
                onChange={(e) => {
                  setExamSearch(e.target.value);
                  setShowExamSearch(true);
                }}
                onFocus={() => setShowExamSearch(true)}
                className="pl-10 h-11 rounded-xl"
              />
            </div>

            {/* Dropdown de Resultados da Busca */}
            {showExamSearch && (
              <div className="bg-card border border-border rounded-xl shadow-xl max-h-72 overflow-y-auto">
                {filteredExams.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground text-sm">
                    Nenhum exame encontrado com o termo informado.
                  </div>
                ) : (
                  filteredExams.map((exam) => (
                    <button
                      key={exam.id || exam.tussCode}
                      onClick={() => handleAddExam(exam)}
                      className="w-full px-4 py-3 text-left hover:bg-muted border-b border-border last:border-b-0 transition-colors flex items-center justify-between group"
                    >
                      <div className="space-y-1 pr-3">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-sm text-foreground group-hover:text-primary">
                            {exam.name}
                          </p>
                          {exam.tussCode && (
                            <Badge variant="outline" className="text-[10px] font-mono bg-muted/60 text-muted-foreground">
                              TUSS: {exam.tussCode}
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          {exam.material && <span>Amostra: {exam.material}</span>}
                          {exam.instructions && <span>&bull; {exam.instructions}</span>}
                        </div>
                      </div>
                      <Plus className="h-4 w-4 text-primary shrink-0 opacity-80 group-hover:opacity-100" />
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Cesta do Pedido Atual (Múltiplos Exames Agrupados) */}
          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between border-b border-border pb-2">
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-primary" />
                <h3 className="font-extrabold text-sm text-foreground">
                  Cesta do Pedido Atual ({selectedExams.length} exame{selectedExams.length !== 1 ? 's' : ''})
                </h3>
              </div>
              {selectedExams.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedExams([])}
                  className="text-xs text-destructive hover:underline font-semibold"
                >
                  Limpar cesta
                </button>
              )}
            </div>

            {selectedExams.length === 0 ? (
              <div className="p-8 border border-dashed border-border rounded-xl text-center bg-muted/10">
                <TestTube className="h-8 w-8 text-muted-foreground/50 mx-auto mb-2" />
                <p className="text-sm font-semibold text-foreground">Nenhum exame adicionado ao pedido</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Selecione acima os exames individuais ou utilize um dos perfis clínicos rápidos.
                </p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {selectedExams.map((exam, idx) => (
                  <div
                    key={exam.id || idx}
                    className="p-3.5 bg-card border border-border rounded-xl hover:border-primary/40 transition-all shadow-xs space-y-2"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2.5">
                        <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                          {idx + 1}
                        </span>
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-bold text-sm text-foreground">{exam.name}</span>
                            {exam.tussCode && (
                              <Badge variant="outline" className="text-[10px] font-mono bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20">
                                TUSS {exam.tussCode}
                              </Badge>
                            )}
                          </div>
                          {exam.instructions && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Orientação: {exam.instructions}
                            </p>
                          )}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveExam(exam.id || exam.tussCode || '')}
                        className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors shrink-0"
                        title="Remover exame da cesta"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Justificativa Individual Opcional */}
                    <div className="pl-7">
                      <input
                        type="text"
                        placeholder="Justificativa clínica específica para este exame (opcional)..."
                        value={exam.justification || ''}
                        onChange={(e) => handleUpdateItemJustification(exam.id, e.target.value)}
                        className="w-full text-xs px-3 py-1.5 rounded-lg border border-border/70 bg-muted/30 placeholder:text-muted-foreground/70 focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Justificativa e Instruções Gerais */}
          <div className="space-y-2">
            <label className="block text-sm font-bold text-foreground">
              Justificativa Clínica e Recomendações Gerais
            </label>
            <textarea
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              placeholder="Descreva a indicação clínica geral (ex: Investigação diagnóstica para queixa de astenia e síndrome metabólica)..."
              rows={3}
              className="w-full px-4 py-2.5 rounded-xl border border-input bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all resize-none shadow-xs"
            />
          </div>

          {/* Prioridade do Pedido */}
          <div>
            <label className="block text-sm font-bold text-foreground mb-2">
              Prioridade Clínica
            </label>
            <div className="grid grid-cols-3 gap-3">
              {(
                [
                  { value: 'ROUTINE', label: 'Rotina', desc: 'Atendimento ambulatorial padrão' },
                  { value: 'URGENT', label: 'Urgente', desc: 'Prioridade em até 48 horas' },
                  { value: 'EMERGENCY', label: 'Emergência', desc: 'Investigação clínica imediata' },
                ] as const
              ).map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setPriority(p.value)}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    priority === p.value
                      ? 'border-primary bg-primary/10 shadow-xs ring-1 ring-primary'
                      : 'border-border bg-card hover:bg-muted/40'
                  }`}
                >
                  <p className="font-bold text-sm text-foreground">{p.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{p.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Ações de Emissão */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/medico/exames')}
              className="rounded-xl h-11 px-5"
            >
              Cancelar
            </Button>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              {/* Salvar Interno */}
              <Button
                type="button"
                variant="outline"
                onClick={handleSubmit}
                disabled={creating || isOpeningMemed || selectedExams.length === 0 || !selectedPatient}
                className="rounded-xl h-11 px-5 font-semibold"
              >
                Salvar Pedido Interno
              </Button>

              {/* Emitir com MeMed e ICP-Brasil */}
              <Button
                type="button"
                onClick={handleEmitWithMemed}
                disabled={creating || isOpeningMemed || selectedExams.length === 0 || !selectedPatient}
                className="gap-2 rounded-xl h-11 px-6 font-bold shadow-md shadow-primary/20 bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {isOpeningMemed ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Carregando MeMed...</span>
                  </>
                ) : (
                  <>
                    <ShieldCheck className="h-4 w-4 text-emerald-400" />
                    <span>Emitir e Assinar (ICP-Brasil)</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
